import { Box, IconButton, TextField, Typography } from "@mui/material";
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from "@mui/icons-material";
import { tablePaginationStyles } from "./TablePagination.styles";
import { useIntl } from "react-intl";

type TTablePagination = {
    currentPage: number;
    totalPages: number;
    pageValue: number | string;
    totalHits: number;
    pageSize: number;
    isSearchAIPagination?: boolean;
    onPageChange: (page: number) => void;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleOnBlur: () => void;
    handleOnKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    inputRef: React.Ref<HTMLInputElement>;
};

const TablePagination = ({
    currentPage,
    totalPages,
    pageValue,
    pageSize,
    isSearchAIPagination = false,
    totalHits,
    onPageChange,
    handleInputChange,
    handleOnBlur,
    handleOnKeyDown,
    inputRef,
}: TTablePagination): JSX.Element => {
    const intl = useIntl();
    const classes = tablePaginationStyles();

    const getResultsRange = () => {
        const start = totalHits ? (currentPage - 1) * pageSize + 1 : 0;
        const end = Math.min(currentPage * pageSize, totalHits);
        return intl.formatMessage({ id: "pagination.range" }, { range: `${start}-${end}`, totalHits });
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: isSearchAIPagination ? "space-between" : "flex-start",
                alignItems: "center",
            }}
        >
            <Typography sx={{ fontSize: 16 }}>{getResultsRange()}</Typography>

            <Box className={classes.paginationWrapper}>
                {totalPages > 1 && (
                    <>
                        <IconButton disabled={currentPage <= 1} onClick={() => onPageChange(1)}>
                            <FirstPage />
                        </IconButton>
                        <IconButton disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
                            <KeyboardArrowLeft />
                        </IconButton>
                        <TextField
                            id="pagination-input"
                            type="number"
                            autoComplete="off"
                            variant="outlined"
                            size="small"
                            value={pageValue}
                            className={classes.paginationInputField}
                            onChange={handleInputChange}
                            onBlur={handleOnBlur}
                            onKeyDown={handleOnKeyDown}
                            inputProps={{
                                pattern: "[0-9]*",
                                min: 1,
                                max: totalPages,
                                style: {
                                    textAlign: "center",
                                },
                            }}
                            InputProps={{
                                style: {
                                    width: 56,
                                    height: 26,
                                    margin: "auto",
                                    fontSize: 14,
                                },
                            }}
                            inputRef={inputRef}
                        />
                        <span style={{ margin: "auto", paddingLeft: "8px" }}>of {totalPages}</span>
                        <IconButton disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
                            <KeyboardArrowRight />
                        </IconButton>
                        <IconButton disabled={currentPage >= totalPages} onClick={() => onPageChange(totalPages)}>
                            <LastPage />
                        </IconButton>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default TablePagination;
